"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Sanitizer = void 0;
const _ = __importStar(require("lodash"));
const Redactor_1 = require("../redactor/Redactor");
class Sanitizer {
    // =============================================================================
    // Static functions
    // =============================================================================
    static registerCustomSanitizer(customSanitizer) {
        Sanitizer.registeredCustomSanitizers.unshift(customSanitizer);
    }
    /**
     * Sanitizes an object recursively for logging
     * - Removes buffer and circular objects
     * - Applies custom sanitizers to reduce noise (optional)
     * - Redacts sensitive fields (optional)
     */
    // eslint-disable-next-line sonarjs/cognitive-complexity
    static sanitize(obj, options) {
        options = _.merge({}, Sanitizer.defaultOptions, options);
        try {
            if (_.isNil(obj)) {
                return obj;
            }
            const seen = new WeakSet();
            const sanitizedJson = JSON.stringify(obj, function (key, value) {
                if (_.isNil(value)) {
                    return value;
                }
                // https://stackoverflow.com/questions/28831391/call-the-replacer-before-the-objects-tojson
                // eslint-disable-next-line no-invalid-this
                const origValue = key ? this[key] : obj;
                const redactor = options.redact ? Redactor_1.Redactor.matchKey(key, options.customRedactors) : undefined;
                // Note: `JSON.stringify` will recurse into the individual array values returned here in the next loop
                if (_.isArray(origValue)) {
                    return redactor ? value.map((item) => redactor.redactValue(item)) : value;
                }
                // Remove buffer objects
                if (_.isBuffer(origValue) || _.isArrayBuffer(origValue)) {
                    return Sanitizer.BUFFER_TEXT;
                }
                // Only dealing with objects from here on
                if (redactor || !_.isObjectLike(origValue)) {
                    return Sanitizer.redactValue(key, value, options, redactor);
                }
                // Remove circular references
                if (seen.has(origValue) && !_.isDate(origValue)) {
                    return Sanitizer.CIRCULAR_TEXT;
                }
                seen.add(origValue);
                const sanitizedValue = Sanitizer.sanitizeObject(origValue, options);
                if (sanitizedValue) {
                    return sanitizedValue;
                }
                return value;
            });
            return JSON.parse(sanitizedJson);
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.warn("Failed to sanitize", error);
            return obj;
        }
    }
    // =============================================================================
    // Helpers
    // =============================================================================
    static sanitizeObject(value, options) {
        for (const customSanitizer of options.customSanitizers) {
            const sanitizedValue = customSanitizer(value);
            if (sanitizedValue !== value) {
                return sanitizedValue;
            }
        }
        for (const customSanitizer of Sanitizer.registeredCustomSanitizers) {
            const sanitizedValue = customSanitizer(value);
            if (sanitizedValue !== value) {
                return sanitizedValue;
            }
        }
        // Special handling to include Error fields that got scrubbed
        if (value instanceof Error) {
            const sanitizedValue = { ...value };
            sanitizedValue.name = value.name;
            sanitizedValue.message = value.message;
            sanitizedValue.stack = value.stack;
            return sanitizedValue;
        }
    }
    static redactValue(key, value, options, redactor) {
        if (redactor) {
            return redactor.redactValue(value);
        }
        if (options.redact && _.isString(value)) {
            // Attempt to parse as JSON for redaction
            // Note: Skip parsing what is clearly not a JSON, as throwing errors are expensive
            if (value.startsWith("{") && value.endsWith("}")) {
                try {
                    const parsedObj = JSON.parse(value);
                    const redactedObj = Sanitizer.redactSanitizedObject(undefined, parsedObj, options);
                    return JSON.stringify(redactedObj);
                }
                catch (error) {
                    // Do nothing
                }
            }
            // Attempt to parse as URL for redaction
            if (Sanitizer.URL_KEY_REGEXP.some((regex) => regex.test(key)) &&
                value.length <= Sanitizer.URL_VALUE_MAX_LENGTH) {
                return value.replace(Sanitizer.urlQueryRegex, (match, matchedKey, matchedValue) => {
                    const redactedValue = Sanitizer.redactSanitizedObject(matchedKey, matchedValue, options);
                    return matchedKey + "=" + redactedValue;
                });
            }
        }
        return value;
    }
    /**
     * This function allows us to redact an object while avoiding another `JSON.stringify` and `JSON.parse`
     * Warning: This has the following assumptions
     * - Redaction is intended
     * - Object is already sanitized
     * - Value can be mutated
     */
    static redactSanitizedObject(key, value, options, redactor) {
        redactor !== null && redactor !== void 0 ? redactor : (redactor = options.redact && key ? Redactor_1.Redactor.matchKey(key, options.customRedactors) : undefined);
        if (_.isArray(value)) {
            return value.map((item) => Sanitizer.redactSanitizedObject(key, item, options, redactor));
        }
        if (redactor || !_.isObjectLike(value)) {
            return Sanitizer.redactValue(key, value, options, redactor);
        }
        const newObject = {};
        for (const valueKey of Object.keys(value)) {
            newObject[valueKey] = Sanitizer.redactSanitizedObject(valueKey, value[valueKey], options);
        }
        return newObject;
    }
}
exports.Sanitizer = Sanitizer;
Sanitizer.registeredCustomSanitizers = new Array();
Sanitizer.urlQueryRegex = new RegExp(/([^&?]*)=([^&]*)/, "g");
Sanitizer.CIRCULAR_TEXT = "[Circular]";
Sanitizer.BUFFER_TEXT = "[Buffer]";
Sanitizer.URL_KEY_REGEXP = [/ur(l|i)(s)?$/i, /path(s)?$/i];
Sanitizer.URL_VALUE_MAX_LENGTH = 2048; // To mitigate ReDoS: Chrome browser supports a maximum URL length of 2 MB as of 2024
(function (Sanitizer) {
    Sanitizer.defaultOptions = {
        redact: true,
        customSanitizers: [],
        customRedactors: [],
    };
})(Sanitizer || (exports.Sanitizer = Sanitizer = {}));
//# sourceMappingURL=Sanitizer.js.map